import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { ChevronLeft, Loader as LoaderIcon } from "lucide-react";
import toast from "react-hot-toast";

import { Field, FieldLabel } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import {
  useCreatePerformanceConfigurationMutation,
  useUpdatePerformanceConfigurationMutation,
  useGetPerformanceConfigurationByIdQuery,
} from "@/services/(company)/api.performance";
import { Spinner } from "@/components/ui/v2/spinner";
import { ENUM_PERFORMANCE_CYCLE_TYPE } from "@/utils/enums/performance.enum";
import { ScheduleFields } from "../../_components/performance-schedule-select";
import { PerformanceEvaluatorsPicker } from "../../_components/performance-evaluators";
import { PerformanceMetricsPicker } from "../../_components/performance-metrics";
import { startCase } from "lodash";
import type { TFunction } from "i18next";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { AccessDenied } from "@/components/permission-guard";

// ─── Validation ───────────────────────────────────────────────────────────────
const buildFormSchema = (t: TFunction) =>
  yup.object({
    startDate: yup
      .date()
      .nullable()
      .required(t("company:performance.v_start_date_required")),
    endDate: yup
      .date()
      .nullable()
      .required(t("company:performance.v_end_date_required")),
    scheduleDay: yup.number().required(t("company:performance.v_day_required")),
    scheduleMonth: yup.number().nullable(),
    evaluators: yup
      .array()
      .of(yup.number())
      .min(1, t("company:performance.v_select_at_least_one_evaluator")),
    performanceMetrics: yup
      .array()
      .of(
        yup.object({
          metricId: yup.number().required(),
          weightage: yup.number().min(1).max(100).required(),
        }),
      )
      .min(1, t("company:performance.v_select_at_least_one_metric")),
  });

export type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Builds ISO string from local date — avoids UTC shift from toISOString()
const toLocalDateString = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T00:00:00.000Z`;
};

const CYCLE_TYPES_WITH_MONTH = [
  ENUM_PERFORMANCE_CYCLE_TYPE.MONTHLY,
  ENUM_PERFORMANCE_CYCLE_TYPE.ANNUALLY,
];

// ─── Component ────────────────────────────────────────────────────────────────
export const PerformanceConfigForm = () => {
  const { t } = useTranslation(["common", "company"]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: teamId, configId } = useParams<{
    id: string;
    configId: string;
  }>();
  const { can, isLoading: isPermissionsLoading } = usePermissions();

  const isUpdating = !!configId;
  const cycleType = searchParams.get("cycleType")!;
  const needsMonth = CYCLE_TYPES_WITH_MONTH.includes(
    cycleType as ENUM_PERFORMANCE_CYCLE_TYPE,
  );

  // ─── Queries ──────────────────────────────────────────────────────────────
  const { data: configByIdData, isFetching: isFetchingConfig } =
    useGetPerformanceConfigurationByIdQuery(
      { id: configId! },
      { skip: !isUpdating },
    );

  const configData = configByIdData?.data;
  const isFetching = isFetchingConfig;

  const [createConfiguration, { isLoading: isCreating }] =
    useCreatePerformanceConfigurationMutation();
  const [updateConfiguration, { isLoading: isUpdatingLoading }] =
    useUpdatePerformanceConfigurationMutation();

  const isLoading = isCreating || isUpdatingLoading;

  // ─── Formik ───────────────────────────────────────────────────────────────
  const formik = useFormik<Fields>({
    enableReinitialize: true,
    validationSchema: buildFormSchema(t),
    initialValues: {
      startDate: configData?.startDate
        ? (new Date(configData.startDate) as any)
        : (null as any),
      endDate: configData?.endDate
        ? (new Date(configData.endDate) as any)
        : (null as any),
      scheduleDay: configData?.scheduleDay ?? 1,
      scheduleMonth: configData?.scheduleMonth ?? 1,
      evaluators: isUpdating
        ? (configByIdData?.data?.evaluators?.map((e) => e.userId) ?? [])
        : [],
      performanceMetrics: isUpdating
        ? (configByIdData?.data?.metrics?.map((m) => ({
            metricId: m.metricId,
            weightage: m.weightage,
          })) ?? [])
        : [],
    },
    onSubmit: async (values) => {
      const toastId = toast.loading(t("company:performance.processing"));
      try {
        const payload = {
          name: t("company:performance.cycle_performance_name", {
            cycle: startCase(cycleType),
          }),
          startDate: toLocalDateString(values.startDate as Date),
          endDate: toLocalDateString(values.endDate as Date),
          cycleType: cycleType as ENUM_PERFORMANCE_CYCLE_TYPE,
          scheduleDay: values.scheduleDay,
          ...(needsMonth && { scheduleMonth: values.scheduleMonth ?? 1 }),
          evaluators: (values.evaluators ?? []) as number[],
          performanceMetrics: (values.performanceMetrics ?? []) as {
            metricId: number;
            weightage: number;
          }[],
        };

        let res;
        if (isUpdating) {
          res = await updateConfiguration({
            configId: configId!,
            payload,
          }).unwrap();
        } else {
          res = await createConfiguration({
            teamId: teamId!,
            payload,
          }).unwrap();
        }

        toast.success(
          res.message ?? t("company:performance.saved_successfully"),
          { id: toastId },
        );
        navigate(-1);
      } catch (e: any) {
        toast.error(
          e?.data?.message ?? t("company:performance.something_went_wrong"),
          { id: toastId },
        );
      }
    },
  });

  // ─── Metrics helpers ──────────────────────────────────────────────────────
  const performanceMetrics = formik.values.performanceMetrics ?? [];

  const totalWeightage = performanceMetrics.reduce(
    (sum, m) => sum + (m.weightage || 0),
    0,
  );

  if (isPermissionsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoaderIcon className="animate-spin text-primary" />
      </div>
    );
  }

  if (isUpdating && !can(PLATFORM_SECTION_ENUM.PERFORMANCE, "edit")) {
    return (
      <AccessDenied
        description={t("company:performance.no_permission_edit_config")}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (!isUpdating && !can(PLATFORM_SECTION_ENUM.PERFORMANCE, "create")) {
    return (
      <AccessDenied
        description={t("company:performance.no_permission_create_config")}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (isFetching) return <Spinner />;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="bg-white border rounded-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="h-9 w-9 flex items-center justify-center rounded-xs border border-border"
        >
          <ChevronLeft size={20} className="text-primary rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-sm font-bold text-gray-900">
            {t("company:performance.cycle_performance_name", {
              cycle: startCase(cycleType),
            })}
          </h2>
          <p className="text-xs text-gray-400">
            {isUpdating
              ? t("company:performance.edit_cycle_settings_desc", {
                  cycle: startCase(cycleType),
                })
              : t("company:performance.configure_cycle_settings_desc", {
                  cycle: startCase(cycleType),
                })}
          </p>
        </div>
      </div>

      {/* Form Body */}
      <form
        onSubmit={formik.handleSubmit}
        className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-6"
      >
        {/* Start & End Date */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DatePickerInput
            label={t("company:performance.start_date_field_label")}
            name={"startDate" satisfies keyof Fields}
            value={
              formik.values.startDate
                ? new Date(formik.values.startDate)
                : undefined
            }
            onChange={(date) =>
              formik.setFieldValue("startDate", date ? date : "")
            }
            error={
              formik.touched.startDate && formik.errors.startDate
                ? (formik.errors.startDate as string)
                : undefined
            }
          />
          <DatePickerInput
            label={t("company:performance.end_date_field_label")}
            name={"endDate" satisfies keyof Fields}
            value={
              formik.values.endDate
                ? new Date(formik.values.endDate)
                : undefined
            }
            onChange={(date) =>
              formik.setFieldValue("endDate", date ? date : "")
            }
            error={
              formik.touched.endDate && formik.errors.endDate
                ? (formik.errors.endDate as string)
                : undefined
            }
          />
        </div>

        {cycleType && (
          <ScheduleFields
            cycleType={cycleType as ENUM_PERFORMANCE_CYCLE_TYPE}
            scheduleDay={formik.values.scheduleDay}
            scheduleMonth={formik.values.scheduleMonth ?? 1}
            onDayChange={(val) => formik.setFieldValue("scheduleDay", val)}
            onMonthChange={(val) => formik.setFieldValue("scheduleMonth", val)}
          />
        )}

        {/* Evaluators */}
        <PerformanceEvaluatorsPicker formik={formik} className="col-span-2" />

        {/* Select Metrics */}
        <PerformanceMetricsPicker formik={formik} className="col-span-2" />

        {/* Total Weightage */}
        <Field className="max-w-lg">
          <FieldLabel className="uppercase text-xs tracking-wide text-gray-500">
            {t("company:performance.total_weightage_label")}
          </FieldLabel>
          <div className="border rounded-xs px-4 py-2.5 bg-gray-50 text-sm text-gray-700 font-medium">
            {totalWeightage}
          </div>
        </Field>
      </form>

      {/* Footer */}
      <div className="border-t px-6 py-4 flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          className="w-32"
          onClick={() => navigate(-1)}
        >
          {t("common:buttons.cancel")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="w-32"
          disabled={
            isLoading ||
            (isUpdating
              ? !can(PLATFORM_SECTION_ENUM.PERFORMANCE, "edit")
              : !can(PLATFORM_SECTION_ENUM.PERFORMANCE, "create"))
          }
          onClick={() => formik.handleSubmit()}
        >
          {isLoading
            ? t("company:performance.saving_cta")
            : t("company:performance.save_cta")}
        </Button>
      </div>
    </div>
  );
};
